import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ServiceSelector = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [recommendation, setRecommendation] = useState(null);

  const questions = [
    {
      id: 'challenge',
      question: 'What is your primary business challenge?',
      subtitle: 'Help us understand your main focus area',
      options: [
        {
          id: 'growth',
          title: 'Accelerating Growth',
          description: 'Need to scale operations, enter new markets, or increase revenue',
          icon: 'TrendingUp',
          color: 'from-green-500 to-emerald-600'
        },
        {
          id: 'efficiency',
          title: 'Improving Efficiency',
          description: 'Streamline processes, reduce costs, or optimize operations',
          icon: 'Zap',
          color: 'from-blue-500 to-blue-600'
        },
        {
          id: 'innovation',
          title: 'Driving Innovation',
          description: 'Develop new products, services, or business models',
          icon: 'Lightbulb',
          color: 'from-purple-500 to-purple-600'
        },
        {
          id: 'transformation',
          title: 'Digital Transformation',
          description: 'Modernize technology, processes, or customer experience',
          icon: 'Smartphone',
          color: 'from-orange-500 to-red-500'
        }
      ]
    },
    {
      id: 'timeline',
      question: 'What is your preferred timeline?',
      subtitle: 'When do you need to see results?',
      options: [
        {
          id: 'immediate',
          title: 'Immediate Impact',
          description: '1-3 months - Quick wins and rapid implementation',
          icon: 'Clock',
          color: 'from-red-500 to-red-600'
        },
        {
          id: 'short',
          title: 'Short Term',
          description: '3-6 months - Focused project with clear deliverables',
          icon: 'Calendar',
          color: 'from-orange-500 to-orange-600'
        },
        {
          id: 'medium',
          title: 'Medium Term',
          description: '6-12 months - Comprehensive transformation initiative',
          icon: 'Target',
          color: 'from-blue-500 to-blue-600'
        },
        {
          id: 'long',
          title: 'Long Term',
          description: '12+ months - Strategic partnership and ongoing evolution',
          icon: 'Map',
          color: 'from-purple-500 to-purple-600'
        }
      ]
    },
    {
      id: 'investment',
      question: 'What is your investment range?',
      subtitle: 'Help us recommend the right scope and approach',
      options: [
        {
          id: 'starter',
          title: 'Starter Package',
          description: '$25K - $75K - Focused solutions and quick implementations',
          icon: 'DollarSign',
          color: 'from-green-500 to-green-600'
        },
        {
          id: 'growth',
          title: 'Growth Package',
          description: '$75K - $200K - Comprehensive projects with multiple phases',
          icon: 'TrendingUp',
          color: 'from-blue-500 to-blue-600'
        },
        {
          id: 'enterprise',
          title: 'Enterprise Package',
          description: '$200K - $500K - Large-scale transformations and integrations',
          icon: 'Building',
          color: 'from-purple-500 to-purple-600'
        },
        {
          id: 'strategic',
          title: 'Strategic Partnership',
          description: '$500K+ - Long-term partnerships and ongoing innovation',
          icon: 'Crown',
          color: 'from-orange-500 to-red-500'
        }
      ]
    }
  ];

  const serviceRecommendations = {
    'growth-immediate-starter': {
      primary: 'Operational Excellence',
      secondary: 'Strategic Consulting',
      description: 'Quick operational improvements to accelerate growth with immediate impact.',
      services: ['Process Optimization', 'Performance Management', 'Quick Win Identification'],
      nextSteps: 'Schedule a 2-hour assessment to identify immediate opportunities.'
    },
    'growth-short-growth': {
      primary: 'Strategic Consulting',
      secondary: 'Digital Transformation',
      description: 'Comprehensive growth strategy with digital enablement for sustainable expansion.',
      services: ['Market Analysis', 'Growth Strategy', 'Digital Marketing', 'Sales Optimization'],
      nextSteps: 'Book a strategic planning workshop to develop your growth roadmap.'
    },
    'efficiency-medium-enterprise': {
      primary: 'Digital Transformation',
      secondary: 'Operational Excellence',
      description: 'Large-scale digital transformation to dramatically improve operational efficiency.',
      services: ['Process Automation', 'System Integration', 'Change Management', 'Training'],
      nextSteps: 'Arrange a comprehensive digital maturity assessment.'
    },
    'innovation-long-strategic': {
      primary: 'Creative Solutions',
      secondary: 'Strategic Consulting',
      description: 'Long-term innovation partnership to continuously develop new opportunities.',
      services: ['Innovation Labs', 'Product Development', 'Market Research', 'Brand Strategy'],
      nextSteps: 'Schedule an innovation workshop to explore possibilities.'
    }
  };

  const handleAnswer = (optionId) => {
    const newAnswers = { ...answers, [questions[currentQuestion].id]: optionId };
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Generate recommendation
      const key = `${newAnswers.challenge}-${newAnswers.timeline}-${newAnswers.investment}`;
      const fallbackKey = `${newAnswers.challenge}-short-growth`;
      const result = serviceRecommendations[key] || serviceRecommendations[fallbackKey] || {
        primary: 'Strategic Consulting',
        secondary: 'Digital Transformation',
        description: 'Custom solution tailored to your specific needs and requirements.',
        services: ['Custom Strategy', 'Tailored Implementation', 'Ongoing Support'],
        nextSteps: 'Schedule a consultation to discuss your unique requirements.'
      };
      
      setRecommendation(result);
      setShowResults(true);
    }
  };

  const resetSelector = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRecommendation(null);
  };

  if (showResults && recommendation) {
    return (
      <div className="py-20 bg-gradient-to-br from-primary/20 to-secondary/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-morph bg-card/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 md:p-12">
            {/* Results Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-brand-green to-brand-cyan rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircle" size={40} className="text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Perfect Match Found!
              </h2>
              <p className="text-lg text-slate-800">
                Based on your responses, here's our personalized recommendation
              </p>
            </div>

            {/* Recommendation */}
            <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl p-8 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Primary Service</h3>
                  <div className="text-2xl font-bold text-brand-primary mb-4">{recommendation.primary}</div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-2">Supporting Service</h4>
                  <div className="text-lg text-brand-secondary font-semibold">{recommendation.secondary}</div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">Why This Combination?</h3>
                  <p className="text-slate-700 leading-relaxed">{recommendation.description}</p>
                </div>
              </div>
            </div>

            {/* Recommended Services */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Recommended Services Include:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {recommendation.services.map((service, index) => (
                  <div key={index} className="flex items-center p-4 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg">
                    <Icon name="CheckCircle2" size={20} className="text-brand-green mr-3 flex-shrink-0" />
                    <span className="font-medium text-slate-800">{service}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-bold text-orange-900 mb-2">Recommended Next Step:</h3>
              <p className="text-orange-800">{recommendation.nextSteps}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Icon name="Calendar" size={20} className="mr-2" />
                Schedule Consultation
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={resetSelector}
              >
                <Icon name="RotateCcw" size={20} className="mr-2" />
                Take Quiz Again
              </Button>
            </div>

            {/* Contact Info */}
            <div className="mt-8 text-center text-sm text-slate-700">
              <p>Questions about your recommendation?</p>
              <p>Call us at <span className="font-semibold text-brand-primary">(555) 123-4567</span> or email <span className="font-semibold text-brand-primary">hello@onsmettle.com</span></p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-20 bg-gradient-to-br from-secondary via-brand-navy to-primary">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass-morph bg-primary/20 backdrop-blur-md rounded-2xl border border-white/10 p-8 md:p-12">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-slate-800 bg-white/90 px-2 py-1 rounded font-body">
                Question {currentQuestion + 1} of {questions.length}
              </span>
              <span className="text-sm font-medium text-slate-800 bg-white/90 px-2 py-1 rounded font-body">
                {Math.round(((currentQuestion + 1) / questions.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-brand-cyan to-brand-purple h-2 rounded-full transition-all duration-500"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {questions[currentQuestion].question}
            </h2>
            <p className="text-lg text-white">
              {questions[currentQuestion].subtitle}
            </p>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {questions[currentQuestion].options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleAnswer(option.id)}
                className="group p-6 glass-morph bg-card/60 backdrop-blur-sm border-2 border-white/20 rounded-xl hover:border-brand-cyan/50 hover:bg-card/80 transition-all duration-300 text-left"
              >
                <div className="flex items-start">
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${option.color} flex items-center justify-center mr-4 flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon name={option.icon} size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-brand-cyan transition-colors duration-300">
                      {option.title}
                    </h3>
                    <p className="text-white text-sm leading-relaxed">
                      {option.description}
                    </p>
                  </div>
                  <Icon name="ChevronRight" size={20} className="text-white group-hover:text-brand-cyan transition-colors duration-300" />
                </div>
              </button>
            ))}
          </div>

          {/* Navigation */}
          {currentQuestion > 0 && (
            <div className="mt-8 flex justify-center">
              <Button
                variant="outline"
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
              >
                <Icon name="ChevronLeft" size={16} className="mr-2" />
                Previous Question
              </Button>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-8 text-center text-sm text-white">
            <p>This assessment takes less than 2 minutes and provides personalized recommendations</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceSelector;